import { z } from "zod";

export const ProbationCycleStatusEnum = z.enum([
  "IN_PROGRESS",
  "COMPLETED",
  "REJECTED",
  "APPROVED",
  "PENDING",
  "EXTEND",
]);

export type ProbationCycleStatus = z.infer<typeof ProbationCycleStatusEnum>;

export const ProbationCycleRowSchema = z
  .object({
    cycleId: z.union([z.number(), z.string()]),
    employeeId: z.union([z.number(), z.string()]),
    employeeCode: z.string().nullish(),
    employeeName: z.string().nullish(),
    designation: z.string().nullish(),
    managerId: z.union([z.number(), z.string()]).nullish(),
    managerName: z.string().nullish(),
    confirmationDate: z.string().nullish(),
    nextConfirmationDate: z.string().nullish(),
    decision: z.string().nullish(),
    status: z.string().nullish(),
    employeeSubmitted: z.boolean().nullish(),
    managerSubmitted: z.boolean().nullish(),
    hrSubmitted: z.boolean().nullish(),
  })
  .passthrough();

export type ProbationCycleRow = z.infer<typeof ProbationCycleRowSchema>;

const springPagedBlockSchema = z
  .object({
    content: z.array(ProbationCycleRowSchema),
    totalPages: z.number().optional(),
    totalElements: z.number().optional(),
  })
  .passthrough();

/** Parses paginated probation cycle list responses (Spring `{ content, totalPages, totalElements }`). */
export function parseProbationCyclesPagedBody(raw: unknown): {
  rows: ProbationCycleRow[];
  totalPages: number;
  totalElements: number;
} {
  const empty = { rows: [] as ProbationCycleRow[], totalPages: 1, totalElements: 0 };
  if (raw == null || typeof raw !== "object") return empty;

  const direct = springPagedBlockSchema.safeParse(raw);
  if (direct.success) {
    return {
      rows: direct.data.content,
      totalPages: direct.data.totalPages ?? 1,
      totalElements: direct.data.totalElements ?? direct.data.content.length,
    };
  }

  const root = raw as Record<string, unknown>;
  const inner = root.data;
  if (inner != null && typeof inner === "object") {
    const parsed = springPagedBlockSchema.safeParse(inner);
    if (parsed.success) {
      return {
        rows: parsed.data.content,
        totalPages: parsed.data.totalPages ?? 1,
        totalElements: parsed.data.totalElements ?? parsed.data.content.length,
      };
    }
    const asArray = z.array(ProbationCycleRowSchema).safeParse(inner);
    if (asArray.success) {
      return {
        rows: asArray.data,
        totalPages: 1,
        totalElements: asArray.data.length,
      };
    }
  }
  return empty;
}
